/**
 * Campaign Workflow Utility
 *
 * Defines the steps and structure for different campaign functional types.
 *
 * Canonical influencer workflow: `influencer_v2` (storyboard-first, Brief Source handoff).
 * Deprecated workflow: `influencer` (script-builder-first) — retained for compatibility with
 * existing campaigns only. No new features should be added to the legacy `influencer` flow.
 */

export type FunctionalType =
    | 'messaging'
    | 'narrative'
    | 'tactical'
    | 'bulk'
    | 'corridor'
    | 'pr'
    | 'search'
    | 'social'
    | 'paid'
    | 'fan_out'
    | 'landing_page'
    | 'influencer'
    | 'influencer_v2'
    | 'paid_ads'
    | 'organic_social'
    | 'sales_outreach'
    | 'heft_persona'
    | 'heft_case_study'
    | 'heft_thought_leadership'
    | 'heft_content_strategy'
    | 'heft_beacons'
    | 'heft_tfd'
    | 'heft_moral_values'
    | 'heft_pr'
    | 'heft_recruiting'
    | 'heft_sales'
    | 'heft_star_deep_dive'

export interface WorkflowStep {
    id: string
    label: string
    path: string
    description?: string
}

// Steps replaced by a Foundation run at campaign creation.
// These IDs are kept for reference by old route entries (hidden but accessible).
export const LEGACY_STRATEGY_STEP_IDS = ['strategy', 'alos', 'targets', 'tfd'] as const

export const FLOW_STEPS: Record<FunctionalType, WorkflowStep[]> = {
    messaging: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'communication-brief', label: 'Communication Brief', path: '/communication-brief' },
        { id: 'topics', label: 'Content Topics', path: '/topics' },
        { id: 'story-map', label: 'Content Map', path: '/story-map' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    narrative: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'story', label: 'Campaign Story', path: '/your-story' },
        { id: 'topics', label: 'Content Topics', path: '/topics' },
        { id: 'story-map', label: 'Campaign Story', path: '/story-map' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    tactical: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'assets', label: 'Asset Builder', path: '/assets' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    bulk: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'dataset', label: 'Dataset', path: '/dataset' },
        { id: 'mapping', label: 'Mapping', path: '/mapping' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    corridor: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'bait', label: 'Attention Hooks', path: '/bait' },
        { id: 'corridor', label: 'Message Sequence', path: '/corridor' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    pr: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'story-library', label: 'Proof Story Library', path: '/story-library' },
        { id: 'newsroom', label: 'Newsroom', path: '/newsroom' },
        { id: 'outreach', label: 'Outreach', path: '/outreach' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    search: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'intent', label: 'Search Intent', path: '/intent' },
        { id: 'pillars', label: 'Pillar Builder', path: '/pillars' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    social: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'voice', label: 'Voice Dial-in', path: '/voice' },
        { id: 'hooks', label: 'Hook Builder', path: '/hooks' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    paid: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'matrix', label: 'Creative Matrix', path: '/matrix' },
        { id: 'variations', label: 'A/B Variations', path: '/variations' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    fan_out: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Your Brand Baseline', path: '/foundation' },
        { id: 'mega-asset', label: 'Core Asset', path: '/mega-asset' },
        { id: 'fan-out', label: 'Derivative Content', path: '/fan-out' },
        { id: 'scheduling', label: 'Send Schedule', path: '/scheduling' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    landing_page: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Foundation', path: '/foundation' },
        { id: 'landing-page-visual', label: 'Visual Profile & Mockup', path: '/landing-page/visual' },
        { id: 'landing-page-goals', label: 'Page Goals & Strategy', path: '/landing-page/goals' },
        { id: 'landing-page-content', label: 'Content', path: '/landing-page/content' },
        { id: 'landing-page-preview', label: 'Preview', path: '/landing-page/preview' },
    ],
    // DEPRECATED: legacy script-builder-first influencer flow.
    // Retained for read-only compatibility with existing campaigns only.
    // Do not add new product features to this flow.
    // Use `influencer_v2` for all new campaigns.
    influencer: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'foundation', label: 'Foundation', path: '/foundation' },
        { id: 'story', label: 'Campaign Story', path: '/your-story' },
        { id: 'script-builder', label: 'Scripts (Legacy)', path: '/script-builder' },
        { id: 'storyboard', label: 'Storyboard (Legacy)', path: '/storyboard' },
        { id: 'brief-package', label: 'Brief Package', path: '/brief-package' },
    ],
    // CANONICAL: storyboard-first influencer flow — used when campaign.source_storyboard_id is set.
    // This is the intended default for all new influencer campaigns.
    // Upstream storyboard is authored in Persuasion Lab; campaign execution adapts it downstream.
    influencer_v2: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'import-storyboard', label: 'Brief Source', path: '/import-storyboard' },
        { id: 'creator-setup', label: 'Creator Setup', path: '/creator-setup' },
        { id: 'moments', label: 'Moment Briefs', path: '/moments' },
        { id: 'brief-package', label: 'Brief Package', path: '/brief-package' },
    ],
    // Brief-derived execution: strategy lives on the Foundation + Communication Brief.
    // Reuse the same execution surfaces as legacy `paid` / `social` / corridor-adjacent flows
    // (routes already exist under /dashboard/campaigns/:id/...).
    paid_ads: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'matrix', label: 'Creative Matrix', path: '/matrix' },
        { id: 'variations', label: 'A/B Variations', path: '/variations' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    organic_social: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'hooks', label: 'Hook Library', path: '/hooks' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    sales_outreach: [
        { id: 'overview', label: 'Overview', path: '' },
        { id: 'outreach', label: 'Outreach', path: '/outreach' },
        { id: 'command-center', label: 'Command Center', path: '/command-center' },
    ],
    // HEFT Story Campaigns — subject-interview-driven persona and deliverable generation.
    heft_persona: [
        { id: 'overview',          label: 'Overview',               path: '',                  description: 'Campaign summary and subject profile' },
        { id: 'story-selection',   label: 'Proof Story Selection',  path: '/story-selection',  description: "Choose proof stories from the subject's story bank" },
        { id: 'identity-snapshot', label: 'Identity Snapshot',      path: '/identity-snapshot', description: 'Curate star points and evidence inventory' },
        { id: 'deliverables',      label: 'Deliverables',           path: '/deliverables',     description: 'Generate bios, hooks, and elevator pitches' },
    ],
    heft_case_study: [
        { id: 'overview',        label: 'Overview',              path: '',                description: 'Campaign summary and subject profile' },
        { id: 'story-selection', label: 'Proof Story Selection', path: '/story-selection', description: "Choose proof transformation stories for the case study" },
        { id: 'case-study',      label: 'Case Study',            path: '/case-study',     description: 'Generate long-form case study and social proof snippets' },
    ],
    heft_thought_leadership: [
        { id: 'overview',          label: 'Overview',               path: '',                  description: 'Campaign summary and subject profile' },
        { id: 'story-selection',   label: 'Proof Story Selection',  path: '/story-selection',  description: "Choose proof stories from the subject's story bank" },
        { id: 'identity-snapshot', label: 'Identity Snapshot',      path: '/identity-snapshot', description: 'Curate star points and evidence inventory' },
        { id: 'deliverables',      label: 'Deliverables',           path: '/deliverables',     description: 'Generate speaker bio, hooks, and talk abstract' },
    ],
    heft_content_strategy: [
        { id: 'overview',          label: 'Overview',               path: '',                  description: 'Campaign summary and subject profile' },
        { id: 'story-selection',   label: 'Proof Story Selection',  path: '/story-selection',  description: "Choose proof stories to feed the content strategy engine" },
        { id: 'content-strategy',  label: 'Content Strategy',       path: '/content-strategy', description: 'Review 22-method strategy map and content ideas' },
    ],
    heft_beacons: [
        { id: 'overview',         label: 'Overview',              path: '',               description: 'Campaign summary and subject profile' },
        { id: 'story-selection',  label: 'Proof Story Selection', path: '/story-selection', description: "Choose supporting proof stories" },
        { id: 'beacons',          label: 'Beacons & Copy',        path: '/beacons',       description: 'Generate emotional copy from beacon signals' },
    ],
    heft_tfd: [
        { id: 'overview',         label: 'Overview',              path: '',        description: 'Campaign summary and subject profile' },
        { id: 'story-selection',  label: 'Proof Story Selection', path: '/story-selection', description: "Choose supporting proof stories" },
        { id: 'tfd',              label: 'Think-Feel-Do',         path: '/tfd',    description: 'Build stage-targeted copy from the Goal Matrix' },
    ],
    heft_moral_values: [
        { id: 'overview',          label: 'Overview',              path: '',                   description: 'Campaign summary and subject profile' },
        { id: 'story-selection',   label: 'Proof Story Selection', path: '/story-selection',   description: "Choose values and belief-tagged proof stories" },
        { id: 'values-messaging',  label: 'Values Messaging',      path: '/values-messaging',  description: 'Generate values-based messaging playbook' },
    ],
    heft_pr: [
        { id: 'overview',          label: 'Overview',              path: '',                  description: 'Campaign summary and subject profile' },
        { id: 'story-selection',   label: 'Proof Story Selection', path: '/story-selection', description: "Choose breakthrough and values proof stories" },
        { id: 'identity-snapshot', label: 'Identity Snapshot',     path: '/identity-snapshot', description: 'Curate star points and evidence' },
        { id: 'pr-deliverables',   label: 'PR Kit',                path: '/pr-deliverables',  description: 'Press release, media bio, journalist Q&A' },
    ],
    heft_recruiting: [
        { id: 'overview',              label: 'Overview',              path: '',                      description: 'Campaign summary and subject profile' },
        { id: 'story-selection',       label: 'Proof Story Selection', path: '/story-selection',    description: "Choose values and leadership proof stories" },
        { id: 'identity-snapshot',     label: 'Identity Snapshot',     path: '/identity-snapshot', description: 'Curate star points and evidence' },
        { id: 'recruiting-deliverables', label: 'Employer Brand',      path: '/recruiting-deliverables', description: 'Job description, culture copy, "why join us"' },
    ],
    heft_sales: [
        { id: 'overview',            label: 'Overview',              path: '',                    description: 'Campaign summary and subject profile' },
        { id: 'story-selection',     label: 'Proof Story Selection', path: '/story-selection',  description: "Choose achievement and innovation proof stories" },
        { id: 'identity-snapshot',   label: 'Identity Snapshot',     path: '/identity-snapshot', description: 'Curate star points and evidence' },
        { id: 'sales-deliverables',  label: 'Sales Enablement',      path: '/sales-deliverables', description: 'Battle card, objection responses, ROI narrative' },
    ],
    heft_star_deep_dive: [
        { id: 'overview',       label: 'Overview',              path: '',             description: 'Campaign summary and subject profile' },
        { id: 'story-selection', label: 'Proof Story Selection', path: '/story-selection', description: "Choose proof stories for the deep dive" },
        { id: 'star-deep-dive', label: 'Star Playbook',         path: '/star-deep-dive', description: 'Per-star-point playbook with proof story and deployment copy' },
    ],
}

/**
 * Get the workflow steps for a specific campaign type
 */
export const getWorkflowSteps = (functionalType: FunctionalType = 'narrative'): WorkflowStep[] => {
    return FLOW_STEPS[functionalType] || FLOW_STEPS.narrative
}

/**
 * Get step information (index and total) for a specific step in a campaign type
 */
export const getStepInfo = (stepId: string, functionalType: FunctionalType = 'narrative') => {
    const steps = getWorkflowSteps(functionalType)
    const index = steps.findIndex(s => s.id === stepId)
    if (index === -1) return null
    return {
        index: index + 1,
        total: steps.length,
        label: steps[index].label,
    }
}

/**
 * Get the subtitle for a campaign page header based on current step
 */
export const getPageSubtitle = (stepId: string, functionalType: FunctionalType = 'narrative') => {
    const info = getStepInfo(stepId, functionalType)
    if (!info) return ''

    const typeLabel = functionalType.charAt(0).toUpperCase() + functionalType.slice(1)
    return `${typeLabel} Campaign - Step ${info.index} of ${info.total}`
}

/**
 * Get the next step in the workflow (Renamed from getNextStep to avoid HMR cache issues)
 */
export const getWorkflowNextStep = (
    stepId: string,
    functionalType: FunctionalType = 'narrative'
): WorkflowStep | null => {
    const steps = getWorkflowSteps(functionalType)
    const index = steps.findIndex(s => s.id === stepId)
    if (index === -1 || index >= steps.length - 1) return null
    return steps[index + 1]
}

/**
 * Get the previous step in the workflow
 */
export const getWorkflowPreviousStep = (
    stepId: string,
    functionalType: FunctionalType = 'narrative'
): WorkflowStep | null => {
    const steps = getWorkflowSteps(functionalType)
    const index = steps.findIndex(s => s.id === stepId)
    if (index <= 0) return null
    return steps[index - 1]
}

/**
 * Get the path for the next step in the workflow
 */
export const getNextStepPath = (
    campaignId: string,
    stepId: string,
    functionalType: FunctionalType = 'narrative'
): string => {
    const nextStep = getWorkflowNextStep(stepId, functionalType)
    if (!nextStep) return `/dashboard/campaigns/${campaignId}` // Default to overview
    return `/dashboard/campaigns/${campaignId}${nextStep.path}`
}

/**
 * Get the path for the previous step in the workflow
 */
export const getPreviousStepPath = (
    campaignId: string,
    stepId: string,
    functionalType: FunctionalType = 'narrative'
): string => {
    const prevStep = getWorkflowPreviousStep(stepId, functionalType)
    if (!prevStep) return `/dashboard/campaigns/${campaignId}` // Default to overview
    return `/dashboard/campaigns/${campaignId}${prevStep.path}`
}
